import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useNotifications } from '../../contexts/NotificationContext'
import { notificacionService } from '../../services/notificacionService'
import Dropdown, { DropdownItem, DropdownDivider } from '../ui/Dropdown'
import { 
  Bell, 
  Check, 
  CheckCheck, 
  AlertCircle,
  Calendar,
  AlertTriangle,
  Info,
  Mail,
  MailOpen,
  Trash2
} from 'lucide-react'

// Función helper para formatear fechas con tiempo exacto
const formatDate = (dateString) => {
  if (!dateString) return ''
  
  try {
    // Intentar parsear la fecha de diferentes formas
    let date
    if (typeof dateString === 'string') {
      // Si viene como string, intentar parsearlo
      date = new Date(dateString)
      
      // Si el parseo falla, intentar con formato ISO
      if (isNaN(date.getTime())) {
        // Intentar agregar 'Z' al final si no lo tiene (asumir UTC)
        const dateStr = dateString.endsWith('Z') ? dateString : dateString + 'Z'
        date = new Date(dateStr)
      }
    } else {
      date = new Date(dateString)
    }
    
    const now = new Date()
    
    // Validar que la fecha sea válida
    if (isNaN(date.getTime())) {
      return ''
    }
    
    // Calcular diferencia en milisegundos
    const diffMs = now.getTime() - date.getTime()
    
    // Si la fecha es en el futuro (por problemas de zona horaria), mostrar "Ahora"
    if (diffMs < 0) {
      return 'Ahora'
    }
    
    const diffSecs = Math.floor(diffMs / 1000)
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)
    const diffWeeks = Math.floor(diffDays / 7)

    // Menos de 1 minuto
    if (diffSecs < 60) return 'Ahora'
    
    // Menos de 1 hora: mostrar minutos exactos
    if (diffMins < 60) {
      return diffMins === 1 ? 'Hace 1 minuto' : `Hace ${diffMins} minutos`
    }
    
    // Menos de 24 horas: mostrar horas y minutos
    if (diffHours < 24) {
      const remainingMins = diffMins % 60
      if (remainingMins === 0) {
        return diffHours === 1 ? 'Hace 1 hora' : `Hace ${diffHours} horas`
      } else {
        const hoursText = diffHours === 1 ? '1 hora' : `${diffHours} horas`
        const minsText = remainingMins === 1 ? '1 minuto' : `${remainingMins} minutos`
        return `Hace ${hoursText} y ${minsText}`
      }
    }
    
    // Menos de 7 días: mostrar días y horas
    if (diffDays < 7) {
      const remainingHours = diffHours % 24
      if (remainingHours === 0) {
        return diffDays === 1 ? 'Hace 1 día' : `Hace ${diffDays} días`
      } else {
        const daysText = diffDays === 1 ? '1 día' : `${diffDays} días`
        const hoursText = remainingHours === 1 ? '1 hora' : `${remainingHours} horas`
        return `Hace ${daysText} y ${hoursText}`
      }
    }
    
    // Menos de 4 semanas: mostrar semanas
    if (diffWeeks < 4) {
      return diffWeeks === 1 ? 'Hace 1 semana' : `Hace ${diffWeeks} semanas`
    }
    
    // Más de 4 semanas: mostrar fecha
    const months = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']
    const day = date.getDate()
    const month = months[date.getMonth()]
    const year = date.getFullYear()
    const currentYear = now.getFullYear()
    
    if (year === currentYear) {
      return `${day} ${month}`
    } else {
      return `${day} ${month} ${year}`
    }
  } catch (error) {
    return ''
  }
}

const getTipoIcon = (tipo) => {
  const icons = {
    'CITA': Calendar,
    'AUTORIZACION': AlertTriangle,
    'SISTEMA': Info
  }
  return icons[tipo] || Bell
}

const getPrioridadColor = (prioridad) => {
  const colors = {
    'BAJA': 'text-gray-500',
    'MEDIA': 'text-blue-500',
    'ALTA': 'text-yellow-500',
    'URGENTE': 'text-red-500'
  }
  return colors[prioridad] || 'text-gray-500'
}

export default function NotificationsDropdown() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { 
    contadorNoLeidas, 
    marcarLeida: marcarLeidaContext,
    marcarTodasLeidas: marcarTodasLeidasContext,
    ocultarNotificacion: ocultarNotificacionContext,
    ocultarTodasNotificaciones,
    registerRefreshListener
  } = useNotifications()
  const [notificaciones, setNotificaciones] = useState([])
  const [loading, setLoading] = useState(false)
  const [marcandoLeida, setMarcandoLeida] = useState(null)
  const timeUpdateRef = useRef(null)
  const [, forceUpdate] = useState(0)

  const fetchNotificaciones = async () => {
    try {
      setLoading(true)
      // Solo obtener notificaciones no leídas para el dropdown
      const response = await notificacionService.getAll({ per_page: 5, page: 1, leida: 'false' })
      
      if (response.success) {
        const data = response.data
        let notifs = []
        if (data && data.data) {
          // Respuesta paginada
          notifs = Array.isArray(data.data) ? data.data.filter(n => !n.leida) : []
        } else if (Array.isArray(data)) {
          // Respuesta como array directo
          notifs = data.filter(n => !n.leida).slice(0, 5)
        } else {
          // No hay datos
          notifs = []
        }
        
        setNotificaciones(notifs)
      } else {
        // Si hay error en la respuesta, establecer array vacío
        setNotificaciones([])
      }
    } catch (err) {
      console.error('Error fetching notificaciones:', err)
      setNotificaciones([])
    } finally {
      setLoading(false)
    }
  }

  // Función para refrescar notificaciones
  const refreshNotifications = useCallback(() => {
    fetchNotificaciones()
  }, [])

  useEffect(() => {
    if (user) {
      // Cargar notificaciones inicialmente con delay para evitar peticiones simultáneas
      const initialTimeout = setTimeout(() => {
        fetchNotificaciones()
      }, 2500) // Delay mayor para que otros componentes carguen primero
      
      // Registrar listener para refrescar cuando se marque como leída en otro lugar
      const unregister = registerRefreshListener(refreshNotifications)
      
      // Actualizar el tiempo relativo cada 5 minutos (solo UI, no peticiones)
      timeUpdateRef.current = setInterval(() => {
        forceUpdate(prev => prev + 1)
      }, 300000) // 5 minutos - solo para actualizar el texto de tiempo relativo

      return () => {
        clearTimeout(initialTimeout)
        unregister()
        if (timeUpdateRef.current) {
          clearInterval(timeUpdateRef.current)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]) // Solo cuando cambia el usuario

  const handleMarcarLeida = async (e, notificacion) => {
    e.stopPropagation()
    const notificacionId = notificacion.id_notificacion || notificacion.idNotificacion
    setMarcandoLeida(notificacionId)
    
    const success = await marcarLeidaContext(notificacionId)
    
    if (success) {
      // Eliminar la notificación del dropdown inmediatamente (ya que solo mostramos no leídas)
      setNotificaciones(prev => prev.filter(n => (n.id_notificacion || n.idNotificacion) !== notificacionId))
    }
    
    setMarcandoLeida(null)
  }

  const handleMarcarTodasLeidas = async () => {
    setLoading(true)
    const success = await marcarTodasLeidasContext()
    
    if (success) {
      // Limpiar todas las notificaciones del dropdown (ya que solo mostramos no leídas)
      setNotificaciones([])
    }
    
    setLoading(false)
  }

  const handleBorrarTodas = async () => {
    if (!window.confirm('¿Borrar todas las notificaciones?')) return
    setLoading(true)
    const ok = await ocultarTodasNotificaciones()
    if (ok) {
      setNotificaciones([])
      setLoading(false)
      return
    }
    // Fallback: borrar en lote lo cargado en dropdown
    const ids = notificaciones.map((n) => n.id_notificacion || n.idNotificacion).filter(Boolean)
    if (ids.length > 0) {
      await Promise.allSettled(ids.map((id) => notificacionService.deleteSoft(id)))
      setNotificaciones([])
    }
    setLoading(false)
  }

  const handleVerTodas = () => {
    navigate('/notificaciones')
  }

  const handleNotificacionClick = async (notificacion) => {
    // Marcar como leída si no lo está antes de navegar
    if (!notificacion.leida) {
      const notificacionId = notificacion.id_notificacion || notificacion.idNotificacion
      const success = await marcarLeidaContext(notificacionId)
      if (success) {
        // Eliminar del dropdown inmediatamente
        setNotificaciones(prev => prev.filter(n => (n.id_notificacion || n.idNotificacion) !== notificacionId))
      }
    }
    navigate('/notificaciones')
  }

  const handleSoftDelete = async (e, notificacion) => {
    e.stopPropagation()
    const notificacionId = notificacion.id_notificacion || notificacion.idNotificacion
    if (!notificacionId) return
    if (!window.confirm('¿Borrar esta notificación?')) return
    const ok = await ocultarNotificacionContext(notificacionId)
    if (ok) {
      setNotificaciones(prev => prev.filter(n => (n.id_notificacion || n.idNotificacion) !== notificacionId))
    }
  }

  const trigger = (
    <button 
      className="relative p-2 rounded-xl text-slate-500 dark:text-gray-400 hover:bg-gray-50 dark:bg-gray-900 dark:hover:bg-gray-700 transition-colors"
      title="Notificaciones"
    >
      <Bell className="h-5 w-5" />
      {contadorNoLeidas > 0 && (
        <span className="absolute top-1 right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-semibold text-white bg-red-500 rounded-full ring-2 ring-white dark:ring-gray-800">
          {contadorNoLeidas > 99 ? '99+' : contadorNoLeidas}
        </span>
      )}
    </button>
  )

  return (
    <Dropdown trigger={trigger} align="right" className="relative" width="w-80 sm:w-96">
      <div className="max-h-[500px] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 dark:border-gray-700 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            Notificaciones
            {contadorNoLeidas > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 rounded-full">
                {contadorNoLeidas} nueva{contadorNoLeidas !== 1 ? 's' : ''}
              </span>
            )}
          </h3>
          {contadorNoLeidas > 0 && (
            <button
              onClick={handleMarcarTodasLeidas}
              disabled={loading}
              className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium disabled:opacity-50"
              title="Marcar todas como leídas"
            >
              <CheckCheck className="w-4 h-4" />
            </button>
          )}
          {contadorNoLeidas > 0 && (
            <button
              onClick={handleBorrarTodas}
              disabled={loading}
              className="text-xs text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 font-medium disabled:opacity-50"
              title="Borrar todas (soft delete)"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Lista de notificaciones */}
        <div className="flex-1 overflow-y-auto">
          {loading && notificaciones.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Cargando...</p>
            </div>
          ) : notificaciones.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <Bell className="w-8 h-8 mx-auto text-gray-400 dark:text-gray-500 mb-2" />
              <p className="text-sm text-gray-500 dark:text-gray-400">No hay notificaciones</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {notificaciones.map((notificacion) => {
                const TipoIcon = getTipoIcon(notificacion.tipo_notificacion)
                const isUnread = !notificacion.leida
                const notificacionId = notificacion.id_notificacion || notificacion.idNotificacion

                return (
                  <div
                    key={notificacionId}
                    className={`px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer ${
                      isUnread ? 'bg-primary-50/30 dark:bg-primary-900/10' : ''
                    }`}
                    onClick={() => handleNotificacionClick(notificacion)}
                  >
                    <div className="flex items-start space-x-3">
                      <div className={`flex-shrink-0 p-1.5 rounded-lg ${
                        isUnread 
                          ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400' 
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                      }`}>
                        <TipoIcon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className={`text-sm font-medium ${
                              isUnread 
                                ? 'text-gray-900 dark:text-white' 
                                : 'text-gray-700 dark:text-gray-300'
                            } line-clamp-1`}>
                              {notificacion.titulo}
                            </p>
                            <p className={`text-xs mt-0.5 ${
                              isUnread 
                                ? 'text-gray-600 dark:text-gray-400' 
                                : 'text-gray-500 dark:text-gray-500'
                            } line-clamp-2`}>
                              {notificacion.mensaje}
                            </p>
                            <div className="flex items-center space-x-2 mt-1">
                              <span className={`text-xs ${getPrioridadColor(notificacion.prioridad)}`}>
                                {notificacion.prioridad}
                              </span>
                              <span className="text-xs text-gray-400 dark:text-gray-500">
                                {formatDate(notificacion.fecha_creacion || notificacion.fechaCreacion || notificacion.created_at)}
                              </span>
                            </div>
                          </div>
                          {isUnread && (
                            <div className="ml-2 flex-shrink-0 flex items-center gap-1">
                              <button
                                onClick={(e) => handleMarcarLeida(e, notificacion)}
                                disabled={marcandoLeida === notificacionId}
                                className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                                title="Marcar como leída"
                                type="button"
                              >
                                {marcandoLeida === notificacionId ? (
                                  <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
                                ) : (
                                  <Check className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                                )}
                              </button>
                              <button
                                onClick={(e) => handleSoftDelete(e, notificacion)}
                                className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                                title="Borrar (soft delete)"
                                type="button"
                              >
                                <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-800 dark:border-gray-700">
          <button
            onClick={handleVerTodas}
            className="w-full text-center text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
          >
            Ver todas las notificaciones
          </button>
        </div>
      </div>
    </Dropdown>
  )
}

